import React from 'react';
import styles from './PrimeArtifactImportDialog.module.css';
import { CANONICAL_PRIME_JSON } from '../../constants/canonicalPrimeFilenames';
import { useIntentMLPrimeImport, MergeStrategy } from './useIntentMLPrimeImport';

interface IntentMLPrimeImportPanelProps {
  onImportComplete: () => void;
}

export const IntentMLPrimeImportPanel: React.FC<IntentMLPrimeImportPanelProps> = ({
  onImportComplete,
}) => {
  const {
    doc,
    mergeStrategy,
    setMergeStrategy,
    validation,
    preview,
    job,
    loading,
    error,
    loadFile,
    runValidate,
    runPreview,
    runApply,
  } = useIntentMLPrimeImport(onImportComplete);

  return (
    <div className={styles.importStep}>
      <h3>IntentML prime_model</h3>
      <p className={styles.formGroup}>
        <small>
          Validate and preview, then apply. Documents with <code>prime_definitions</code> write to the
          prime model tables. Uses the same merge strategies as the backend seed importer. Prefer
          authoritative repo files (e.g. <code>{CANONICAL_PRIME_JSON.krysalisPlatform}</code>), not
          superseded <code>*_final.json</code> repair copies — see backend{' '}
          <code>project_file_inventory.md</code>.
        </small>
      </p>

      <div className={styles.formGroup}>
        <label htmlFor="intentml-json">IntentML JSON file</label>
        <input id="intentml-json" type="file" accept=".json,application/json" onChange={loadFile} disabled={loading} />
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="intentml-merge">Merge strategy</label>
        <select
          id="intentml-merge"
          value={mergeStrategy}
          onChange={(e) => setMergeStrategy(e.target.value as MergeStrategy)}
          disabled={loading}
        >
          <option value="MERGE">MERGE</option>
          <option value="SKIP_EXISTING">SKIP_EXISTING</option>
          <option value="UPDATE_EXISTING">UPDATE_EXISTING</option>
          <option value="CREATE_NEW">CREATE_NEW</option>
        </select>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.stepActions}>
        <button type="button" className={styles.secondaryButton} onClick={runValidate} disabled={loading || !doc}>
          {loading ? '…' : 'Validate'}
        </button>
        <button type="button" className={styles.secondaryButton} onClick={runPreview} disabled={loading || !doc}>
          Preview
        </button>
        <button type="button" className={styles.primaryButton} onClick={runApply} disabled={loading || !doc}>
          {loading ? 'Applying…' : 'Apply'}
        </button>
      </div>

      {validation && (
        <div className={styles.formGroup}>
          <strong>Validation</strong>
          <small>
            {' '}
            valid={String(validation.valid)} — {validation.diagnostics.length} diagnostic(s)
          </small>
        </div>
      )}

      {preview && (
        <div className={styles.formGroup}>
          <strong>Preview hash</strong>
          <small> {preview.preview_hash}</small>
        </div>
      )}

      {job && (
        <div className={styles.formGroup}>
          <strong>Job</strong>
          <small>
            {' '}
            {job.status} — see result_summary in API response (import counts when prime_definitions present).
          </small>
        </div>
      )}
    </div>
  );
};
